import * as THREE from 'three';
import {
  canRankUp,
  getHeroDef,
  heroStats,
  type AbilitySlot,
  type HeroState,
  type Simulation,
} from '@moba/shared';
import { abilityIcon } from './abilityIcons.ts';
import type { Input } from './input.ts';

import { t } from './i18n.ts';
/**
 * Controles táctiles para jugar en el móvil en horizontal (estilo MOBA móvil):
 *
 * - Joystick a la izquierda: aparece donde se apoya el pulgar (mitad izquierda
 *   de la pantalla) y mueve al héroe en esa dirección de la pantalla.
 * - Botones a la derecha: ATAQUE fija el enemigo más cercano a tiro (el héroe
 *   lo persigue y encadena el combo), Q/W/E/R y volver a base.
 *   Un toque lanza la habilidad hacia el objetivo fijado o el enemigo más
 *   cercano a su alcance (si no hay, hacia donde mira); arrastrar desde el
 *   botón apunta a mano con una guía en el suelo y se lanza al soltar
 *   (arrastrar de vuelta al botón cancela). El `+` sube el rango.
 */

const SLOTS: readonly AbilitySlot[] = ['q', 'w', 'e', 'r'];
/** radio del joystick (px) */
const JOY_RADIUS = 58;
/** arrastre mínimo (px) para pasar de toque a apuntado */
const AIM_THRESHOLD = 22;
/** arrastre (px) que equivale al alcance completo de la habilidad */
const AIM_FULL = 110;

export interface TouchControlsDeps {
  input: Input;
  camera: () => THREE.Camera;
  sim: () => Simulation;
  /** guía de apuntado en el suelo (null = ocultar) */
  showAim: (from: { x: number; z: number } | null, to?: { x: number; z: number }, radius?: number) => void;
  onPause: () => void;
  /** barra de menús táctil: tienda, marcador, chat (sólo online) y emojis */
  onShop: () => void;
  onBoard: () => void;
  onChat: () => void;
  onEmote: () => void;
  /** ¿hay chat de partida? (online) */
  chatAvailable: () => boolean;
}

interface SlotButton {
  root: HTMLButtonElement;
  cd: HTMLElement;
  cdText: HTMLElement;
  charges: HTMLElement;
  plus: HTMLButtonElement;
}

interface AimState {
  slot: AbilitySlot;
  pointerId: number;
  startX: number;
  startY: number;
  aiming: boolean;
  cancelled: boolean;
  target: { x: number; z: number };
}

/** ¿Pantalla táctil como puntero principal? (`?touch=1` lo fuerza para probar en escritorio) */
export function isTouchDevice(): boolean {
  const forced = new URLSearchParams(window.location.search).get('touch');
  if (forced === '1') return true;
  if (forced === '0') return false;
  return window.matchMedia('(pointer: coarse)').matches || (navigator.maxTouchPoints > 0 && !window.matchMedia('(pointer: fine)').matches);
}

export class TouchControls {
  private readonly root: HTMLElement;
  private readonly joyZone: HTMLElement;
  private readonly joyBase: HTMLElement;
  private readonly joyKnob: HTMLElement;
  private readonly attackButton: HTMLButtonElement;
  private readonly recallButton: HTMLButtonElement;
  private readonly slots = new Map<AbilitySlot, SlotButton>();
  private joyPointer: number | null = null;
  private joyCenter = { x: 0, y: 0 };
  private aim: AimState | null = null;
  private heroId = '';
  private visible = false;
  private readonly right = new THREE.Vector3();
  private readonly up = new THREE.Vector3();

  constructor(private readonly deps: TouchControlsDeps) {
    const root = document.createElement('div');
    root.id = 'touch-controls';
    root.hidden = true;
    root.innerHTML = `
      <div id="touch-joy-zone"><div id="touch-joy"><div id="touch-joy-knob"></div></div></div>
      <div id="touch-actions">
        <button type="button" class="touch-btn touch-attack" aria-label="${t('Atacar')}">${t('ATAQUE')}</button>
        ${SLOTS.map(
          (slot) => `<div class="touch-slot" data-slot="${slot}">
            <button type="button" class="touch-btn touch-ability" data-slot="${slot}" aria-label="${t('Habilidad {key}', { key: slot.toUpperCase() })}">
              <span class="touch-icon"></span>
              <span class="touch-cd"></span>
              <span class="touch-cd-text"></span>
              <span class="touch-charges"></span>
              <span class="touch-key">${slot.toUpperCase()}</span>
            </button>
            <button type="button" class="touch-plus" data-slot="${slot}" aria-label="${t('Subir rango {key}', { key: slot.toUpperCase() })}" hidden>+</button>
          </div>`,
        ).join('')}
        <button type="button" class="touch-btn touch-recall" aria-label="${t('Volver a base')}">${t('BASE')}</button>
      </div>
      <button type="button" id="touch-pause" aria-label="${t('Pausa')}">II</button>
      <div id="touch-menu">
        <button type="button" class="touch-menu-btn" data-action="shop" aria-label="${t('Tienda')}"><i class="tm-icon tm-shop"></i><span>${t('TIENDA')}</span></button>
        <button type="button" class="touch-menu-btn" data-action="board" aria-label="${t('Marcador')}"><i class="tm-icon tm-board"></i><span>${t('MARCADOR')}</span></button>
        <button type="button" class="touch-menu-btn" data-action="emote" aria-label="${t('Emojis')}"><i class="tm-icon tm-emote">☺</i></button>
        <button type="button" class="touch-menu-btn" data-action="chat" aria-label="${t('Chat')}" hidden><i class="tm-icon tm-chat"></i></button>
        <button type="button" class="touch-menu-btn" data-action="friends" aria-label="${t('Amigos')}" hidden><i class="tm-icon tm-friends"></i></button>
      </div>
    `;
    document.getElementById('ui')?.appendChild(root);
    this.root = root;
    this.joyZone = root.querySelector('#touch-joy-zone') as HTMLElement;
    this.joyBase = root.querySelector('#touch-joy') as HTMLElement;
    this.joyKnob = root.querySelector('#touch-joy-knob') as HTMLElement;
    this.attackButton = root.querySelector('.touch-attack') as HTMLButtonElement;
    this.recallButton = root.querySelector('.touch-recall') as HTMLButtonElement;
    for (const slot of SLOTS) {
      const box = root.querySelector(`.touch-slot[data-slot="${slot}"]`) as HTMLElement;
      this.slots.set(slot, {
        root: box.querySelector('.touch-ability') as HTMLButtonElement,
        cd: box.querySelector('.touch-cd') as HTMLElement,
        cdText: box.querySelector('.touch-cd-text') as HTMLElement,
        charges: box.querySelector('.touch-charges') as HTMLElement,
        plus: box.querySelector('.touch-plus') as HTMLButtonElement,
      });
    }
    this.bind();
  }

  // ------------------------------------------------------------------ eventos

  private bind(): void {
    const { input } = this.deps;
    // nada de menús contextuales / zoom / selección al mantener pulsado
    this.root.addEventListener('contextmenu', (event) => event.preventDefault());

    this.joyZone.addEventListener('pointerdown', (event) => {
      if (this.joyPointer !== null) return;
      event.preventDefault();
      this.joyPointer = event.pointerId;
      this.joyZone.setPointerCapture(event.pointerId);
      this.joyCenter = { x: event.clientX, y: event.clientY };
      this.joyBase.style.left = `${event.clientX}px`;
      this.joyBase.style.top = `${event.clientY}px`;
      this.joyBase.classList.add('active');
      this.moveJoystick(event.clientX, event.clientY);
    });
    this.joyZone.addEventListener('pointermove', (event) => {
      if (event.pointerId !== this.joyPointer) return;
      event.preventDefault();
      this.moveJoystick(event.clientX, event.clientY);
    });
    const endJoy = (event: PointerEvent) => {
      if (event.pointerId !== this.joyPointer) return;
      this.joyPointer = null;
      this.joyBase.classList.remove('active');
      this.joyKnob.style.transform = 'translate(-50%, -50%)';
      // vuelve a su sitio de reposo
      this.joyBase.style.left = '';
      this.joyBase.style.top = '';
      input.setJoystick(null);
    };
    this.joyZone.addEventListener('pointerup', endJoy);
    this.joyZone.addEventListener('pointercancel', endJoy);

    this.attackButton.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      const player = this.deps.sim().player;
      if (!player?.alive) return;
      const range = getHeroDef(player.heroId).attacks[0].range * heroStats(player.heroId, player.level, player.items).attackRange;
      // a tiro o un poco más allá (el héroe se acerca solo); si no, el más cercano en pantalla
      const locked = input.lockNearest(range + 3) || input.lockNearest(14);
      this.flash(this.attackButton, locked ? 'ok' : 'miss');
    });
    this.recallButton.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      input.pressRecall();
      this.flash(this.recallButton, 'ok');
    });
    (this.root.querySelector('#touch-pause') as HTMLButtonElement).addEventListener('click', () => this.deps.onPause());
    for (const button of this.root.querySelectorAll<HTMLButtonElement>('.touch-menu-btn')) {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const action = button.dataset.action;
        if (action === 'shop') this.deps.onShop();
        else if (action === 'board') this.deps.onBoard();
        else if (action === 'chat') this.deps.onChat();
        else if (action === 'emote') this.deps.onEmote();
        // amigos: el mismo botón del panel del jugador (oculto en táctil)
        else if (action === 'friends') document.getElementById('friends-hud-btn')?.click();
      });
    }

    for (const [slot, button] of this.slots) {
      button.plus.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        event.stopPropagation();
        input.requestLevelUp(slot);
      });
      const el = button.root;
      el.addEventListener('pointerdown', (event) => {
        if (this.aim) return;
        event.preventDefault();
        el.setPointerCapture(event.pointerId);
        this.aim = { slot, pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, aiming: false, cancelled: false, target: { x: 0, z: 0 } };
        el.classList.add('pressed');
      });
      el.addEventListener('pointermove', (event) => {
        const aim = this.aim;
        if (!aim || aim.pointerId !== event.pointerId) return;
        event.preventDefault();
        this.updateAim(aim, event.clientX, event.clientY);
      });
      el.addEventListener('pointerup', (event) => {
        const aim = this.aim;
        if (!aim || aim.pointerId !== event.pointerId) return;
        this.finishAim(aim, true);
      });
      el.addEventListener('pointercancel', (event) => {
        const aim = this.aim;
        if (!aim || aim.pointerId !== event.pointerId) return;
        this.finishAim(aim, false);
      });
    }
  }

  /** Vector de pantalla (px, y hacia abajo) -> dirección en el suelo vista desde la cámara. */
  private screenToGround(dx: number, dy: number): { x: number; z: number } | null {
    const camera = this.deps.camera();
    this.right.setFromMatrixColumn(camera.matrixWorld, 0);
    this.up.setFromMatrixColumn(camera.matrixWorld, 1);
    this.right.y = 0;
    this.up.y = 0;
    if (this.right.lengthSq() < 1e-6 || this.up.lengthSq() < 1e-6) return null;
    this.right.normalize();
    this.up.normalize();
    const x = this.right.x * dx - this.up.x * dy;
    const z = this.right.z * dx - this.up.z * dy;
    const length = Math.hypot(x, z);
    return length > 1e-6 ? { x: x / length, z: z / length } : null;
  }

  private moveJoystick(x: number, y: number): void {
    let dx = x - this.joyCenter.x;
    let dy = y - this.joyCenter.y;
    const distance = Math.hypot(dx, dy);
    if (distance > JOY_RADIUS) {
      // el joystick sigue al pulgar si se sale del círculo
      this.joyCenter.x += (dx / distance) * (distance - JOY_RADIUS);
      this.joyCenter.y += (dy / distance) * (distance - JOY_RADIUS);
      this.joyBase.style.left = `${this.joyCenter.x}px`;
      this.joyBase.style.top = `${this.joyCenter.y}px`;
      dx = (dx / distance) * JOY_RADIUS;
      dy = (dy / distance) * JOY_RADIUS;
    }
    this.joyKnob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
    // zona muerta para no andar por un roce
    const direction = Math.hypot(dx, dy) > JOY_RADIUS * 0.18 ? this.screenToGround(dx, dy) : null;
    this.deps.input.setJoystick(direction);
  }

  private reach(player: HeroState, slot: AbilitySlot): number {
    return Math.max(2, this.deps.sim().abilityReach(getHeroDef(player.heroId).abilities[slot]));
  }

  private updateAim(aim: AimState, x: number, y: number): void {
    const player = this.deps.sim().player;
    if (!player) return;
    const dx = x - aim.startX;
    const dy = y - aim.startY;
    const drag = Math.hypot(dx, dy);
    if (!aim.aiming && drag < AIM_THRESHOLD) return;
    const button = this.slots.get(aim.slot)?.root;
    // volver con el dedo encima del botón cancela el apuntado
    if (aim.aiming && button) {
      const rect = button.getBoundingClientRect();
      aim.cancelled = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
      button.classList.toggle('cancel', aim.cancelled);
    }
    aim.aiming = true;
    const direction = this.screenToGround(dx, dy);
    if (!direction) return;
    const reach = this.reach(player, aim.slot);
    const distance = reach * THREE.MathUtils.clamp(drag / AIM_FULL, 0.25, 1);
    aim.target = { x: player.pos.x + direction.x * distance, z: player.pos.z + direction.z * distance };
    this.deps.showAim(aim.cancelled ? null : player.pos, aim.target, 0.6);
  }

  private finishAim(aim: AimState, release: boolean): void {
    this.aim = null;
    const button = this.slots.get(aim.slot)?.root;
    button?.classList.remove('pressed', 'cancel');
    this.deps.showAim(null);
    if (!release || aim.cancelled) return;
    const player = this.deps.sim().player;
    if (!player?.alive) return;
    const target = aim.aiming ? aim.target : this.autoAim(player, aim.slot);
    this.deps.input.castAt(aim.slot, target.x, target.z);
  }

  /**
   * Toque sin arrastrar: el objetivo fijado si está a tiro; si no, el héroe
   * enemigo más cercano a su alcance; si no, cualquier enemigo a su alcance; y
   * si tampoco, recto hacia donde mira el héroe.
   */
  private autoAim(player: HeroState, slot: AbilitySlot): { x: number; z: number } {
    const reach = this.reach(player, slot);
    const sim = this.deps.sim();
    const enemies = this.deps.input.enemyTargets.filter((unit) => unit.alive);
    const dist = (unit: { pos: { x: number; z: number } }) => Math.hypot(unit.pos.x - player.pos.x, unit.pos.z - player.pos.z);
    const locked = enemies.find((unit) => unit.id === this.deps.input.targetId && dist(unit) <= reach + 3);
    if (locked) return { x: locked.pos.x, z: locked.pos.z };
    const heroIds = new Set(sim.heroes.filter((hero) => hero.team !== player.team).map((hero) => hero.id));
    const inReach = enemies.filter((unit) => dist(unit) <= reach + unit.radius + 1).sort((a, b) => dist(a) - dist(b));
    const pick = inReach.find((unit) => heroIds.has(unit.id)) ?? inReach[0];
    if (pick) return { x: pick.pos.x, z: pick.pos.z };
    return { x: player.pos.x + Math.sin(player.facing) * reach, z: player.pos.z + Math.cos(player.facing) * reach };
  }

  private flash(element: HTMLElement, kind: 'ok' | 'miss'): void {
    element.classList.remove('flash-ok', 'flash-miss');
    void element.offsetWidth;
    element.classList.add(kind === 'ok' ? 'flash-ok' : 'flash-miss');
  }

  // ------------------------------------------------------------------ cada frame

  /** Muestra u oculta los controles y refresca enfriamientos, cargas y rangos. */
  update(player: HeroState | undefined, active: boolean): void {
    const show = active && Boolean(player);
    if (show !== this.visible) {
      this.visible = show;
      this.root.hidden = !show;
      if (!show) this.release();
    }
    if (!show || !player) return;
    const chat = this.root.querySelector('[data-action="chat"]') as HTMLButtonElement;
    const chatOn = this.deps.chatAvailable();
    if (chat.hidden === chatOn) chat.hidden = !chatOn;
    const friends = this.root.querySelector('[data-action="friends"]') as HTMLButtonElement;
    const hudFriends = document.getElementById('friends-hud-btn');
    const friendsOn = Boolean(hudFriends && !hudFriends.hidden);
    if (friends.hidden === friendsOn) friends.hidden = !friendsOn;
    // aviso de solicitudes pendientes (la insignia del botón original)
    const badge = hudFriends?.querySelector('.badge')?.textContent ?? '';
    if (friends.dataset.badge !== badge) {
      friends.dataset.badge = badge;
      friends.classList.toggle('has-badge', badge !== '');
    }
    if (player.heroId !== this.heroId) {
      this.heroId = player.heroId;
      // las ilustraciones son URLs data: (las mismas de la barra de habilidades)
      for (const [slot, button] of this.slots) {
        (button.root.querySelector('.touch-icon') as HTMLElement).style.backgroundImage = `url("${abilityIcon(player.heroId, slot)}")`;
      }
      this.attackButton.style.setProperty('--attack-icon', `url("${abilityIcon(player.heroId, 'attack')}")`);
    }
    const def = getHeroDef(player.heroId);
    for (const [slot, button] of this.slots) {
      const rank = player.ranks[slot];
      const cooldown = player.cooldowns[slot];
      const maxCharges = def.abilities[slot].kind === 'projectile' ? Math.max(1, (def.abilities[slot] as { projectile: { charges?: number } }).projectile.charges ?? 1) : 1;
      const charges = player.charges?.[slot] ?? 0;
      const ready = rank > 0 && (cooldown <= 0 || (maxCharges > 1 && charges > 0));
      button.root.classList.toggle('locked', rank <= 0);
      button.root.classList.toggle('cooling', rank > 0 && !ready);
      const seconds = Math.ceil(cooldown / 30);
      const text = rank > 0 && !ready && seconds > 0 ? String(seconds) : '';
      if (button.cdText.textContent !== text) button.cdText.textContent = text;
      const chargeText = maxCharges > 1 && rank > 0 ? String(charges) : '';
      if (button.charges.textContent !== chargeText) button.charges.textContent = chargeText;
      button.plus.hidden = !canRankUp(player, slot);
    }
    this.attackButton.classList.toggle('engaged', this.deps.input.targetId !== 0);
    this.root.classList.toggle('dead', !player.alive);
  }

  /** Suelta todo (al pausar, morir la partida o perder el foco). */
  release(): void {
    if (this.joyPointer !== null) {
      this.joyPointer = null;
      this.joyBase.classList.remove('active');
      this.joyKnob.style.transform = 'translate(-50%, -50%)';
    }
    this.deps.input.setJoystick(null);
    if (this.aim) this.finishAim(this.aim, false);
  }
}
